import TechnicianAssignmentField from '@/components/serviceTicket/assignment/TechnicianAssignmentField';
import {
  useGetServiceTicketQuery,
  useUpsertServiceTicketMutation,
} from '@/store/api/services/operations/serviceTickets/serviceTickets';
import type { AssignmentType } from '@/types/serviceTicket';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  FormGroup,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

interface AssignTechnicianServiceTicketProps extends ModalChildrenProps {
  service_ticket_id?: string;
}

const AssignTechnicianServiceTicket = ({ hide, props }: AssignTechnicianServiceTicketProps) => {
  const serviceTicketId = props?.service_ticket_id;

  // Fetch current service ticket data
  const { data: serviceTicketData } = useGetServiceTicketQuery(serviceTicketId || '', {
    skip: !serviceTicketId,
  });

  const [updateServiceTicket, { isLoading }] = useUpsertServiceTicketMutation();

  const currentTicket = serviceTicketData?.data;

  const [assignmentType, setAssignmentType] = useState<AssignmentType>('internal');
  const [technicianId, setTechnicianId] = useState('');
  const [opsServiceId, setOpsServiceId] = useState('');

  // Preselect the current assignee once the ticket loads. An external ticket opens on External
  // with its vendor selected.
  useEffect(() => {
    if (!currentTicket) return;
    if (currentTicket.ops_service_id) {
      setAssignmentType('external');
      setOpsServiceId(currentTicket.ops_service_id);
      setTechnicianId('');
    } else {
      setAssignmentType('internal');
      setOpsServiceId('');
      setTechnicianId(
        currentTicket.technician_id && Number(currentTicket.technician_id) !== 0
          ? currentTicket.technician_id.toString()
          : '',
      );
    }
  }, [currentTicket]);

  const handleAssignmentTypeChange = (value: AssignmentType) => {
    setAssignmentType(value);
    setTechnicianId('');
    setOpsServiceId('');
  };

  const isExternal = assignmentType === 'external';
  const hasSelection = isExternal ? !!opsServiceId : !!technicianId;

  const handleSave = async () => {
    if (!serviceTicketId) {
      toast.error('Service ticket ID is required');
      return;
    }

    if (!hasSelection) {
      toast.error(isExternal ? 'Please select an Ops Service' : 'Please select a technician');
      return;
    }

    if (!currentTicket) {
      toast.error('Service ticket data not available');
      return;
    }

    try {
      // Send only the chosen assignee. Boro clears the other one.
      await updateServiceTicket(
        isExternal
          ? { id: serviceTicketId, ops_service_id: opsServiceId }
          : { id: serviceTicketId, technician_id: Number(technicianId) },
      ).unwrap();

      toast.success(
        isExternal ? 'Ops Service assigned successfully' : 'Technician assigned successfully',
      );
      hide();
    } catch (error: any) {
      const errorMessage =
        error?.meta?.message || error?.data?.message || 'Failed to assign technician';
      toast.error(errorMessage);
      console.error('Error assigning technician:', error);
    }
  };

  return (
    <>
      <DialogContent sx={{ my: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Please select a technician or Ops Service to assign to this ticket
        </Typography>
        <FormGroup sx={{ mb: 2 }}>
          <TechnicianAssignmentField
            assignmentType={assignmentType}
            technicianId={technicianId}
            opsServiceId={opsServiceId}
            onAssignmentTypeChange={handleAssignmentTypeChange}
            onTechnicianChange={setTechnicianId}
            onOpsServiceChange={setOpsServiceId}
          />
        </FormGroup>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button variant="text" onClick={hide}>
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={!hasSelection || isLoading}
        >
          {isLoading ? 'Assigning...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </>
  );
};

export default AssignTechnicianServiceTicket;
